import { ApolloClient, InMemoryCache, createHttpLink, ApolloLink, fromPromise } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { configService } from '../services/configService';
import { authService } from '../services/authService';
import { createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';

// キャッシュの設定
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        // ページネーション対応のフィールド
        listUsers: {
          // 既存のデータと新しいデータをマージする
          keyArgs: ['companyId'],
          merge(existing = { items: [], nextToken: null }, incoming, { args }) {
            // nextTokenが指定されている場合は、既存のデータに追加
            // そうでない場合は、新しいデータで上書き
            if (args?.nextToken) {
              return {
                items: [...(existing.items || []), ...(incoming.items || [])],
                nextToken: incoming.nextToken,
              };
            } else {
              return incoming;
            }
          },
        },
        listScenarios: {
          keyArgs: ['companyId'],
          merge(existing = { items: [], nextToken: null }, incoming, { args }) {
            // nextTokenが指定されている場合は、既存のデータに追加
            // そうでない場合は、新しいデータで上書き
            if (args?.nextToken) {
              return {
                items: [...(existing.items || []), ...(incoming.items || [])],
                nextToken: incoming.nextToken,
              };
            } else {
              return incoming;
            }
          },
        },
        listEvaluationsByCompany: {
          keyArgs: ['companyId'],
          merge(existing = { items: [], nextToken: null }, incoming, { args }) {
            // nextTokenが指定されている場合は、既存のデータに追加
            // そうでない場合は、新しいデータで上書き
            if (args?.nextToken) {
              return {
                items: [...(existing.items || []), ...(incoming.items || [])],
                nextToken: incoming.nextToken,
              };
            } else {
              return incoming;
            }
          },
        },
      },
    },
  },
});

// Apollo Clientのインスタンスを作成する関数
export const createApolloClient = () => {
  const config = configService.getConfig();
  
  // トークンの更新処理
  let isRefreshing = false;
  let pendingRequests: Function[] = [];

  // トークンの更新が完了したら、保留中のリクエストを再実行
  const resolvePendingRequests = () => {
    pendingRequests.forEach((callback) => callback());
    pendingRequests = [];
  };

  // 認証ヘッダーを追加
  const authLink = setContext(async (_, { headers }) => {
    // トークンの有効期限をチェック
    if (authService.isTokenExpired()) {
      // トークンを更新
      try {
        const result = await authService.refreshToken();
        if (!result.success) {
          console.error('トークンの更新に失敗しました:', result.message);
        }
      } catch (error) {
        console.error('トークンの更新中にエラーが発生しました:', error);
      }
    }

    // ローカルストレージからトークンを取得
    const token = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
    
    // ヘッダーにトークンを追加
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });
  
  // エラーハンドリングリンク
  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        // トークンの期限切れエラーを検出
        if (err.message === 'Token has expired.' || err.extensions?.code === 'UNAUTHENTICATED') {
          // 既にトークンの更新中であれば、保留中のリクエストに追加
          if (isRefreshing) {
            return fromPromise(
              new Promise<void>((resolve) => {
                pendingRequests.push(() => resolve());
              })
            ).flatMap(() => {
              // リクエストを再実行
              return forward(operation);
            });
          }

          isRefreshing = true;

          // トークンを更新
          return fromPromise(
            authService.refreshToken()
              .then((result) => {
                if (!result.success) {
                  // トークンの更新に失敗した場合はログアウト
                  if (result.requireRelogin) {
                    // ログイン画面にリダイレクト
                    window.location.href = '/login';
                  }
                  return;
                }
                
                // 保留中のリクエストを再実行
                resolvePendingRequests();
                return;
              })
              .catch((error) => {
                console.error('トークンの更新中にエラーが発生しました:', error);
                // ログイン画面にリダイレクト
                window.location.href = '/login';
                return;
              })
              .finally(() => {
                isRefreshing = false;
              })
          ).flatMap(() => {
            // リクエストを再実行
            return forward(operation);
          });
        }

        // その他のGraphQLエラーをログに出力
        const msg = `[GraphQL error]: ${err.message}`;
        console.error(msg, err);
      }
    }

    if (networkError) {
      const msg = `[Network error]: ${networkError}`;
      console.error(msg);
    }
  });
  
  // 通常のHTTPリンク
  const httpLink = createHttpLink({
    uri: config.graphqlApiUrl,
  });
  
  // AWS AppSync用の認証リンク
  const appSyncAuthLink = createAuthLink({
    url: config.graphqlApiUrl,
    region: 'ap-northeast-1', // リージョンを固定値として設定
    auth: {
      type: 'AMAZON_COGNITO_USER_POOLS',
      jwtToken: () => localStorage.getItem('idToken') || sessionStorage.getItem('idToken') || '',
    }
  });
  
  // AWS AppSync用のサブスクリプションリンク
  const appSyncSubscriptionLink = createSubscriptionHandshakeLink({
    url: config.graphqlApiUrl,
    region: 'ap-northeast-1', // リージョンを固定値として設定
    auth: {
      type: 'AMAZON_COGNITO_USER_POOLS',
      jwtToken: () => localStorage.getItem('idToken') || sessionStorage.getItem('idToken') || '',
    }
  });
  
  // HTTP操作とサブスクリプション操作を分割するリンク
  const link = ApolloLink.split(
    operation => {
      const operationType = operation.query.definitions.find(
        def => def.kind === 'OperationDefinition'
      );
      return !!operationType && (operationType as any).operation === 'subscription';
    },
    appSyncSubscriptionLink,
    ApolloLink.from([errorLink, authLink, httpLink])
  );
  
  // Apollo Clientのインスタンスを作成
  return new ApolloClient({
    link,
    cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
      query: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  });
};

// Apollo Clientのシングルトンインスタンス
let apolloClient: ApolloClient<any> | null = null;

// Apollo Clientのインスタンスを取得する関数
export const getApolloClient = () => {
  if (!apolloClient) {
    apolloClient = createApolloClient();
  }
  return apolloClient;
};
